import { Alert, type FormControlProps, Grid } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { UUID } from "../../../utils/uuid";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useToTarget } from "../../to/use-to-target";
import { useSegmentSelector } from "../hooks/use-segment-selector";
import { ConnectionSelect } from "./connection-select";
import { SegmentFilters } from "./moment-segment-filters/segment-filters";
import { SegmentSelectAutocomplete } from "./segment-select-autocomplete";

export type SegmentFormProps = FormControlProps & {
  showValidationErrors: boolean;
  connectionCategories: ConnectionCategory[];
  restrictedSegments: UUID[];
  segmentLabel?: string;
  segmentPlaceholder?: string;
};

export const SegmentForm: FC<SegmentFormProps> = ({
  disabled,
  showValidationErrors,
  connectionCategories,
  restrictedSegments,
  segmentLabel,
  segmentPlaceholder,
}) => {
  const [errorToDisplay, setErrorToDisplay] = useState<string>();

  const segmentSelector = useSegmentSelector(setErrorToDisplay);
  const momentFormContext = useFormContext<MomentFormMoment>();
  const { toTarget } = useToTarget();

  const handleConfirm = useCallback(
    (segment: MomentFormMoment["segment"]) => {
      momentFormContext.setValue("segment", segment);
      void momentFormContext.trigger("segment");
    },
    [momentFormContext],
  );

  if (errorToDisplay) return <Alert severity="error">{errorToDisplay}</Alert>;

  return (
    <>
      {toTarget === "connection" && (
        <Grid size={{ xs: 12 }} pt={2}>
          <ConnectionSelect disabled={disabled} connectionCategories={connectionCategories} />
        </Grid>
      )}
      <Grid size={{ xs: 12 }} pt={2}>
        <SegmentSelectAutocomplete
          disabled={disabled}
          label={segmentLabel}
          placeholder={segmentPlaceholder}
          restrictedSegments={restrictedSegments}
          segmentSelectorInfo={segmentSelector}
        />
      </Grid>

      {!segmentSelector.loading && (
        <SegmentFilters
          selectedSegment={segmentSelector.data.selectedSegment}
          isNew={segmentSelector.data.isNew}
          disabled={disabled}
          showValidationErrors={showValidationErrors}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
